"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ActivityLogParams } from "@/components/profile-activity/ProfileActivityLogs";
import type { ApiProfileRepRatesState, RatingWithProfileInfoAndLevel } from "@/entities/IProfile";
import { SortDirection } from "@/entities/ISort";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { ApiProfileProxyActionType } from "@/generated/models/ApiProfileProxyActionType";
import { amIUser, formatNumberWithCommas } from "@/helpers/Helpers";
import type { Page } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { ProfileRatersParamsOrderBy, RateMatter } from "@/types/enums";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useMemo, useState } from "react";
import UserPageIdentityHeaderCICRate from "../identity/header/cic-rate/UserPageIdentityHeaderCICRate";
import UserPageIdentityStatementsAddButton from "../identity/statements/add/UserPageIdentityStatementsAddButton";
import UserPageIdentityStatements from "../identity/statements/UserPageIdentityStatements";
import UserPageRateWrapper from "../utils/rate/UserPageRateWrapper";
import UserCICStatus from "../utils/user-cic-status/UserCICStatus";
import UserCICTypeIcon from "../utils/user-cic-type/UserCICTypeIcon";
import TopRaterAvatars from "./header/TopRaterAvatars";
import UserPageRepModifyModal from "./modify-rep/UserPageRepModifyModal";
import GrantRepDialog from "./new-rep/GrantRepDialog";
import RepCategoryPill from "./RepCategoryPill";
import UserPageCombinedActivityLog from "./UserPageCombinedActivityLog";
import { getCanEditRep, sortRepsByRatingAndContributors } from "./UserPageRep.helpers";
type MobileTab = "rep" | "identity";
export default function UserPageRepMobile(t0) {
  const $ = _c(131);
  const {
    profile,
    repRates,
    initialActivityLogParams
  } = t0;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const profileHandle = profile.handle ?? "";
  const [activeTab, setActiveTab] = useState("rep");
  const [isGrantRepOpen, setIsGrantRepOpen] = useState(false);
  const [isNicRateOpen, setIsNicRateOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [editCategory, setEditCategory] = useState(null);
  let t1;
  let t2;
  if ($[0] !== profileHandle) {
    t1 = [QueryKey.PROFILE_RATERS, {
      handleOrWallet: profileHandle,
      matter: RateMatter.NIC,
      page: 1,
      pageSize: 1,
      order: SortDirection.DESC,
      orderBy: ProfileRatersParamsOrderBy.RATING,
      given: false
    }];
    t2 = async () => await commonApiFetch({
      endpoint: `profiles/${profileHandle}/cic/ratings/by-rater`,
      params: {
        page: "1",
        page_size: "1",
        order: SortDirection.DESC.toLowerCase(),
        order_by: ProfileRatersParamsOrderBy.RATING.toLowerCase(),
        given: "false"
      }
    });
    $[0] = profileHandle;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!profileHandle;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: nicRatings
  } = useQuery(t4);
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const mq = globalThis.matchMedia("(min-width: 1024px)");
      const handler = e => {
        if (e.matches) {
          setIsGrantRepOpen(false);
          setIsNicRateOpen(false);
        }
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    };
    t6 = [];
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setVisibleCount(5);
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = repRates?.rating_stats;
  let t9;
  if ($[10] !== t8) {
    t9 = [t8];
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  useEffect(t7, t9);
  repRates?.rating_stats;
  let t10;
  if ($[12] !== repRates?.rating_stats) {
    t10 = repRates?.rating_stats ?? [];
    $[12] = repRates?.rating_stats;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10) {
    t11 = sortRepsByRatingAndContributors(t10);
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const reps = t11;
  let t12;
  if ($[16] !== activeProfileProxy || $[17] !== connectedProfile || $[18] !== profile) {
    t12 = getCanEditRep({
      myProfile: connectedProfile,
      targetProfile: profile,
      activeProfileProxy
    });
    $[16] = activeProfileProxy;
    $[17] = connectedProfile;
    $[18] = profile;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const canEditRep = t12;
  let t13;
  bb0: {
    if (!connectedProfile?.handle) {
      t13 = false;
      break bb0;
    }
    if (activeProfileProxy) {
      if (profile.handle === activeProfileProxy.created_by.handle) {
        t13 = false;
        break bb0;
      }
      let t14;
      if ($[20] !== activeProfileProxy.actions) {
        t14 = activeProfileProxy.actions.some(_temp);
        $[20] = activeProfileProxy.actions;
        $[21] = t14;
      } else {
        t14 = $[21];
      }
      t13 = t14;
      break bb0;
    }
    if (amIUser({
      profile,
      address
    })) {
      t13 = false;
      break bb0;
    }
    t13 = true;
  }
  const canEditNic = t13;
  let t14;
  if ($[22] !== activeProfileProxy || $[23] !== address || $[24] !== profile.handle || $[25] !== profile.wallets) {
    t14 = !activeProfileProxy && !!profile?.handle && (profile.wallets ?? []).some(w => w.wallet.toLowerCase() === address?.toLowerCase());
    $[22] = activeProfileProxy;
    $[23] = address;
    $[24] = profile.handle;
    $[25] = profile.wallets;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const canEditStatements = t14;
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setActiveTab("rep");
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const t16 = `tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${activeTab === "rep" ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t17 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${activeTab === "rep" ? "tw-border-primary-300/90 tw-bg-transparent tw-ring-2 tw-ring-primary-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t18;
  if ($[28] !== activeTab) {
    t18 = activeTab === "rep" && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-primary-300 tw-transition-all tw-duration-300" />;
    $[28] = activeTab;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t17 || $[31] !== t18) {
    t19 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t17}>{t18}</span></div>;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== activeTab) {
    t20 = activeTab === "rep" && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-blue-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /></>;
    $[33] = activeTab;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const t21 = `tw-relative tw-transition-opacity tw-duration-300 ${activeTab === "rep" ? "" : "tw-opacity-40"}`;
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">Total Rep</div>;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== repRates) {
    t23 = repRates ? formatNumberWithCommas(repRates.total_rep_rating) : "\u2014";
    $[36] = repRates;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t23) {
    t24 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-primary-400">{t23}</div>;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== repRates) {
    t25 = repRates && <div className="tw-mt-2.5 tw-flex tw-items-center"><span className="tw-text-xs tw-font-normal tw-text-iron-400">{formatNumberWithCommas(repRates.number_of_raters)}{" "}{repRates.number_of_raters === 1 ? "rater" : "raters"}</span></div>;
    $[40] = repRates;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t21 || $[43] !== t24 || $[44] !== t25) {
    t26 = <div className={t21}>{t22}{t24}{t25}</div>;
    $[42] = t21;
    $[43] = t24;
    $[44] = t25;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t16 || $[47] !== t19 || $[48] !== t20 || $[49] !== t26) {
    t27 = <button type="button" onClick={t15} className={t16}>{t19}{t20}{t26}</button>;
    $[46] = t16;
    $[47] = t19;
    $[48] = t20;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setActiveTab("identity");
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  const t29 = `tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${activeTab === "identity" ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t30 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${activeTab === "identity" ? "tw-border-emerald-300/90 tw-bg-transparent tw-ring-2 tw-ring-emerald-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t31;
  if ($[52] !== activeTab) {
    t31 = activeTab === "identity" && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-300 tw-transition-all tw-duration-300" />;
    $[52] = activeTab;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== t30 || $[55] !== t31) {
    t32 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t30}>{t31}</span></div>;
    $[54] = t30;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== activeTab) {
    t33 = activeTab === "identity" && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-emerald-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /></>;
    $[57] = activeTab;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  const t34 = `tw-relative tw-transition-opacity tw-duration-300 ${activeTab === "identity" ? "" : "tw-opacity-40"}`;
  let t35;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">NIC</div>;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== profile.cic) {
    t36 = formatNumberWithCommas(profile.cic);
    $[60] = profile.cic;
    $[61] = t36;
  } else {
    t36 = $[61];
  }
  let t37;
  if ($[62] !== t36) {
    t37 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-white">{t36}</div>;
    $[62] = t36;
    $[63] = t37;
  } else {
    t37 = $[63];
  }
  let t38;
  if ($[64] !== profile.cic) {
    t38 = <span className="tw-h-4 tw-w-4 tw-flex-shrink-0"><UserCICTypeIcon cic={profile.cic} /></span>;
    $[64] = profile.cic;
    $[65] = t38;
  } else {
    t38 = $[65];
  }
  let t39;
  if ($[66] !== profile.cic) {
    t39 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-emerald-400"><UserCICStatus cic={profile.cic} /></span>;
    $[66] = profile.cic;
    $[67] = t39;
  } else {
    t39 = $[67];
  }
  let t40;
  if ($[68] !== t38 || $[69] !== t39) {
    t40 = <div className="tw-mt-2.5 tw-flex tw-items-center tw-gap-1.5">{t38}{t39}</div>;
    $[68] = t38;
    $[69] = t39;
    $[70] = t40;
  } else {
    t40 = $[70];
  }
  const t41 = profile.handle ?? "";
  let t42;
  if ($[71] !== t41) {
    t42 = <div className="tw-pointer-events-none desktop-hover:tw-pointer-events-auto"><TopRaterAvatars handleOrWallet={t41} matter={RateMatter.NIC} count={3} size="sm" withLinks={false} /></div>;
    $[71] = t41;
    $[72] = t42;
  } else {
    t42 = $[72];
  }
  const t43 = nicRatings?.count ?? 0;
  let t44;
  if ($[73] !== t43) {
    t44 = formatNumberWithCommas(t43);
    $[73] = t43;
    $[74] = t44;
  } else {
    t44 = $[74];
  }
  const t45 = (nicRatings?.count ?? 0) === 1 ? "rater" : "raters";
  let t46;
  if ($[75] !== t44 || $[76] !== t45) {
    t46 = <span className="tw-text-xs tw-font-normal tw-text-iron-400">{t44}{" "}{t45}</span>;
    $[75] = t44;
    $[76] = t45;
    $[77] = t46;
  } else {
    t46 = $[77];
  }
  let t47;
  if ($[78] !== t42 || $[79] !== t46) {
    t47 = <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">{t42}{t46}</div>;
    $[78] = t42;
    $[79] = t46;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  let t48;
  if ($[81] !== t34 || $[82] !== t37 || $[83] !== t40 || $[84] !== t47) {
    t48 = <div className={t34}>{t35}{t37}{t40}{t47}</div>;
    $[81] = t34;
    $[82] = t37;
    $[83] = t40;
    $[84] = t47;
    $[85] = t48;
  } else {
    t48 = $[85];
  }
  let t49;
  if ($[86] !== t29 || $[87] !== t32 || $[88] !== t33 || $[89] !== t48) {
    t49 = <button type="button" onClick={t28} className={t29}>{t32}{t33}{t48}</button>;
    $[86] = t29;
    $[87] = t32;
    $[88] = t33;
    $[89] = t48;
    $[90] = t49;
  } else {
    t49 = $[90];
  }
  let t50;
  if ($[91] !== t27 || $[92] !== t49) {
    t50 = <div className="tw-grid tw-grid-cols-2 tw-gap-3">{t27}{t49}</div>;
    $[91] = t27;
    $[92] = t49;
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  let t51;
  if ($[94] !== activeTab || $[95] !== canEditNic || $[96] !== canEditRep || $[97] !== canEditStatements || $[98] !== initialActivityLogParams || $[99] !== profile || $[100] !== reps || $[101] !== visibleCount) {
    t51 = <AnimatePresence mode="wait">{activeTab === "rep" ? <motion.div key="rep" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: "easeInOut"
      }}>{reps.length > 0 && <div className="tw-mt-4"><div className="tw-mb-3 tw-whitespace-nowrap tw-text-xs tw-uppercase tw-tracking-wider tw-text-iron-100 tw-font-semibold">Rep Categories</div><div className="tw-flex tw-flex-wrap tw-gap-2">{reps.slice(0, visibleCount).map(rep => <RepCategoryPill key={rep.category} rep={rep} profileHandle={profile.handle ?? ""} canEdit={canEditRep} onEdit={setEditCategory} compact={true} />)}{reps.length > visibleCount && <button type="button" onClick={() => setVisibleCount(_temp2)} className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700/60 tw-bg-iron-900/60 tw-px-4 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-iron-400 tw-transition-colors hover:tw-border-iron-600/60 hover:tw-bg-iron-800/60 hover:tw-text-iron-300">+{reps.length - visibleCount} more</button>}</div></div>}{canEditRep && <div className="tw-mt-4"><UserPageRateWrapper profile={profile} type={RateMatter.REP} hideOwnProfileMessage={true}><div className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-blue-500/20 tw-bg-blue-400/5 tw-px-5 tw-py-3"><span className="tw-text-xs tw-font-medium tw-text-blue-300/70">Add rep to this identity</span><button onClick={() => setIsGrantRepOpen(true)} className="tw-flex tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-primary-500 tw-bg-primary-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-blue-500/20 tw-transition tw-duration-300 tw-ease-out hover:tw-border-primary-600 hover:tw-bg-primary-600 md:tw-py-3"><svg className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>Grant Rep</button></div></UserPageRateWrapper></div>}<div className="tw-mt-6"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} matter={RateMatter.REP} withMatterFilter={false} /></div></motion.div> : <motion.div key="identity" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: "easeInOut"
      }}>{canEditNic && <div className="tw-mt-4"><UserPageRateWrapper profile={profile} type={RateMatter.NIC} hideOwnProfileMessage={true}><div className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-emerald-500/10 tw-bg-emerald-500/5 tw-px-5 tw-py-3"><span className="tw-text-xs tw-font-medium tw-text-emerald-200/70">Verify this identity</span><button onClick={() => setIsNicRateOpen(true)} className="tw-flex tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-emerald-500 tw-bg-emerald-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-emerald-500/20 tw-transition tw-duration-300 tw-ease-out hover:tw-border-emerald-400 hover:tw-bg-emerald-400 sm:tw-py-3">Rate NIC</button></div></UserPageRateWrapper></div>}<div className="tw-mt-6 tw-flex tw-items-center tw-justify-between"><h3 className="tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-100">ID Statements</h3>{canEditStatements && <UserPageIdentityStatementsAddButton profile={profile} />}</div><div className="tw-mt-3 tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#0f1014]"><UserPageIdentityStatements profile={profile} /></div><div className="tw-mt-6"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} matter={RateMatter.NIC} withMatterFilter={false} /></div></motion.div>}</AnimatePresence>;
    $[94] = activeTab;
    $[95] = canEditNic;
    $[96] = canEditRep;
    $[97] = canEditStatements;
    $[98] = initialActivityLogParams;
    $[99] = profile;
    $[100] = reps;
    $[101] = visibleCount;
    $[102] = t51;
  } else {
    t51 = $[102];
  }
  let t52;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = () => setIsGrantRepOpen(false);
    $[103] = t52;
  } else {
    t52 = $[103];
  }
  let t53;
  if ($[104] !== isGrantRepOpen || $[105] !== profile || $[106] !== repRates) {
    t53 = <GrantRepDialog profile={profile} repRates={repRates} isOpen={isGrantRepOpen} onClose={t52} />;
    $[104] = isGrantRepOpen;
    $[105] = profile;
    $[106] = repRates;
    $[107] = t53;
  } else {
    t53 = $[107];
  }
  let t54;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = () => setIsNicRateOpen(false);
    $[108] = t54;
  } else {
    t54 = $[108];
  }
  let t55;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = () => setIsNicRateOpen(false);
    $[109] = t55;
  } else {
    t55 = $[109];
  }
  let t56;
  if ($[110] !== profile) {
    t56 = <UserPageIdentityHeaderCICRate profile={profile} isTooltip={false} onSuccess={t55} />;
    $[110] = profile;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== profile || $[113] !== t56) {
    t57 = <UserPageRateWrapper profile={profile} type={RateMatter.NIC}>{t56}</UserPageRateWrapper>;
    $[112] = profile;
    $[113] = t56;
    $[114] = t57;
  } else {
    t57 = $[114];
  }
  let t58;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="tw-mt-3"><button onClick={() => setIsNicRateOpen(false)} type="button" className="tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button></div>;
    $[115] = t58;
  } else {
    t58 = $[115];
  }
  let t59;
  if ($[116] !== t57) {
    t59 = <div className="tw-px-4 sm:tw-px-6">{t57}{t58}</div>;
    $[116] = t57;
    $[117] = t59;
  } else {
    t59 = $[117];
  }
  let t60;
  if ($[118] !== isNicRateOpen || $[119] !== t59) {
    t60 = <MobileWrapperDialog title="Rate NIC" isOpen={isNicRateOpen} onClose={t54} tabletModal={true}>{t59}</MobileWrapperDialog>;
    $[118] = isNicRateOpen;
    $[119] = t59;
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] !== canEditRep || $[122] !== editCategory || $[123] !== profile) {
    t61 = canEditRep && editCategory && <UserPageRepModifyModal profile={profile} category={editCategory} onClose={() => setEditCategory(null)} />;
    $[121] = canEditRep;
    $[122] = editCategory;
    $[123] = profile;
    $[124] = t61;
  } else {
    t61 = $[124];
  }
  let t62;
  if ($[125] !== t50 || $[126] !== t51 || $[127] !== t53 || $[128] !== t60 || $[129] !== t61) {
    t62 = <div className="lg:tw-hidden">{t50}{t51}{t53}{t60}{t61}</div>;
    $[125] = t50;
    $[126] = t51;
    $[127] = t53;
    $[128] = t60;
    $[129] = t61;
    $[130] = t62;
  } else {
    t62 = $[130];
  }
  return t62;
}
function _temp2(prev) {
  return prev + 10;
}
function _temp(action) {
  return action.action_type === ApiProfileProxyActionType.AllocateCic;
}